import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import type {
  JobOfferClauses,
  JobOfferFields,
  JobOfferLetter,
  JobOfferLetterStatus,
  JobOfferPolicyAck,
  JobOfferSection,
  JobOfferSignature,
} from '../types/hiring';
import { assembleEmploymentAgreementSections, assembleJobOfferSections } from './jobOfferLetter';

export const JOB_OFFER_LETTERS = 'jobOfferLetters';

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseSignature(raw: unknown): JobOfferSignature | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.dataUrl !== 'string' || !s.dataUrl) return null;
  return {
    dataUrl: s.dataUrl,
    signedBy: String(s.signedBy || ''),
    signedAt: parseDate(s.signedAt) || new Date(),
    title: typeof s.title === 'string' ? s.title : undefined,
  };
}

function parseFields(raw: unknown): JobOfferFields {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    employeeFirstName: String(r.employeeFirstName || ''),
    employeeLastName: String(r.employeeLastName || ''),
    employeeEmail: String(r.employeeEmail || ''),
    positionTitle: String(r.positionTitle || ''),
    duties: String(r.duties || ''),
    startDate: String(r.startDate || ''),
    payType: r.payType === 'hourly' ? 'hourly' : 'salary',
    payAmount: typeof r.payAmount === 'number' ? r.payAmount : Number(r.payAmount) || 0,
    hoursPerWeek: typeof r.hoursPerWeek === 'number' ? r.hoursPerWeek : 40,
    employmentType: (r.employmentType as JobOfferFields['employmentType']) || 'full-time',
    location: String(r.location || ''),
    reportingTo: String(r.reportingTo || ''),
    reportingToUserId: String(r.reportingToUserId || ''),
    responseDeadline: String(r.responseDeadline || ''),
    ceoName: String(r.ceoName || ''),
    ceoTitle: String(r.ceoTitle || ''),
  };
}

function parseClauses(raw: unknown): JobOfferClauses {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    travelInstall: r.travelInstall === true,
    salaryNoOvertime: r.salaryNoOvertime === true,
    hourlyOvertime: r.hourlyOvertime === true,
    profitSharePercent: typeof r.profitSharePercent === 'number' ? r.profitSharePercent : null,
    sepIraPercent: typeof r.sepIraPercent === 'number' ? r.sepIraPercent : null,
    healthReimbursementMonthly: typeof r.healthReimbursementMonthly === 'number' ? r.healthReimbursementMonthly : null,
    executiveResponsibilities: r.executiveResponsibilities === true,
    executiveResponsibilitiesText: String(r.executiveResponsibilitiesText || ''),
    salesCommission: r.salesCommission === true,
    salesCommissionPercent: typeof r.salesCommissionPercent === 'number' ? r.salesCommissionPercent : null,
  };
}

export function parseJobOfferLetter(id: string, raw: Record<string, unknown>): JobOfferLetter {
  const fields = parseFields(raw.fields);
  const clauses = parseClauses(raw.clauses);
  const sections = Array.isArray(raw.sections)
    ? (raw.sections as JobOfferSection[])
    : assembleJobOfferSections(fields, clauses);
  const agreementSections = Array.isArray(raw.agreementSections)
    ? (raw.agreementSections as JobOfferSection[])
    : assembleEmploymentAgreementSections(fields, clauses);
  const policyAcks: JobOfferPolicyAck[] = Array.isArray(raw.policyAcks)
    ? (raw.policyAcks as Record<string, unknown>[]).map(a => ({
      policyId: String(a.policyId || ''),
      policyTitle: String(a.policyTitle || ''),
      version: String(a.version || ''),
      acknowledgedAt: parseDate(a.acknowledgedAt) || new Date(),
    }))
    : [];
  return {
    id,
    applicationId: String(raw.applicationId || ''),
    slug: String(raw.slug || ''),
    status: (raw.status as JobOfferLetterStatus) || 'draft',
    fields,
    clauses,
    sections,
    agreementSections,
    ceoSignature: parseSignature(raw.ceoSignature),
    employeeSignature: parseSignature(raw.employeeSignature),
    agreementCeoSignature: parseSignature(raw.agreementCeoSignature) || parseSignature(raw.ceoSignature),
    agreementEmployeeSignature: parseSignature(raw.agreementEmployeeSignature),
    policyAcks,
    startDateConfirmedAt: parseDate(raw.startDateConfirmedAt),
    sentAt: parseDate(raw.sentAt),
    contractSentAt: Object.prototype.hasOwnProperty.call(raw, 'contractSentAt')
      ? parseDate(raw.contractSentAt)
      : undefined,
    offerSignedAt: parseDate(raw.offerSignedAt),
    signedAt: parseDate(raw.signedAt),
    signedPdfUrl: typeof raw.signedPdfUrl === 'string' ? raw.signedPdfUrl : null,
    createdBy: String(raw.createdBy || ''),
    createdAt: parseDate(raw.createdAt) || new Date(),
    updatedAt: parseDate(raw.updatedAt) || new Date(),
  };
}

export function createOfferSlug(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export function subscribeJobOfferLetters(
  onData: (offers: JobOfferLetter[]) => void,
): Unsubscribe {
  const q = query(collection(db, JOB_OFFER_LETTERS), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseJobOfferLetter(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('jobOfferLettersFirestore', JOB_OFFER_LETTERS, err),
  );
}

export function subscribeLatestJobOffer(
  applicationId: string,
  onData: (offer: JobOfferLetter | null) => void,
): Unsubscribe {
  const q = query(
    collection(db, JOB_OFFER_LETTERS),
    where('applicationId', '==', applicationId),
    orderBy('createdAt', 'desc'),
    limit(8),
  );
  return onSnapshot(
    q,
    snap => {
      const rows = snap.docs
        .map(d => parseJobOfferLetter(d.id, d.data() as Record<string, unknown>))
        .filter(o => o.status !== 'voided');
      onData(rows[0] || null);
    },
    err => firestoreSnapshotListenError('jobOfferLettersFirestore', JOB_OFFER_LETTERS, err),
  );
}

export async function createJobOfferDraft(input: {
  applicationId: string;
  fields: JobOfferFields;
  clauses: JobOfferClauses;
}): Promise<string> {
  const uid = auth.currentUser?.uid || '';
  const sections = assembleJobOfferSections(input.fields, input.clauses);
  const agreementSections = assembleEmploymentAgreementSections(input.fields, input.clauses);
  const ref = await addDoc(collection(db, JOB_OFFER_LETTERS), {
    applicationId: input.applicationId,
    slug: createOfferSlug(),
    status: 'draft',
    fields: input.fields,
    clauses: input.clauses,
    sections,
    agreementSections,
    ceoSignature: null,
    employeeSignature: null,
    agreementCeoSignature: null,
    agreementEmployeeSignature: null,
    policyAcks: [],
    startDateConfirmedAt: null,
    sentAt: null,
    contractSentAt: null,
    offerSignedAt: null,
    signedAt: null,
    signedPdfUrl: null,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function saveJobOfferDraft(
  id: string,
  input: { fields: JobOfferFields; clauses: JobOfferClauses; ceoSignature?: JobOfferSignature | null },
): Promise<void> {
  const payload: Record<string, unknown> = {
    fields: input.fields,
    clauses: input.clauses,
    sections: assembleJobOfferSections(input.fields, input.clauses),
    agreementSections: assembleEmploymentAgreementSections(input.fields, input.clauses),
    updatedAt: serverTimestamp(),
  };
  if (input.ceoSignature !== undefined) {
    const ceo = input.ceoSignature
      ? {
        dataUrl: input.ceoSignature.dataUrl,
        signedBy: input.ceoSignature.signedBy,
        signedAt: input.ceoSignature.signedAt,
        title: input.ceoSignature.title || null,
      }
      : null;
    payload.ceoSignature = ceo;
    payload.agreementCeoSignature = ceo;
  }
  await updateDoc(doc(db, JOB_OFFER_LETTERS, id), payload as DocumentData);
}

export async function sendJobOfferDraft(id: string): Promise<void> {
  await updateDoc(doc(db, JOB_OFFER_LETTERS, id), {
    status: 'sent',
    sentAt: serverTimestamp(),
    contractSentAt: null,
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function sendJobOfferContract(id: string): Promise<void> {
  await updateDoc(doc(db, JOB_OFFER_LETTERS, id), {
    contractSentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function voidJobOffer(id: string): Promise<void> {
  await updateDoc(doc(db, JOB_OFFER_LETTERS, id), {
    status: 'voided',
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function getJobOfferBySlugClient(slug: string): Promise<JobOfferLetter | null> {
  const q = query(collection(db, JOB_OFFER_LETTERS), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return parseJobOfferLetter(snap.docs[0].id, snap.docs[0].data() as Record<string, unknown>);
}
